import React, { useEffect, useMemo, useRef, useState } from "react"
import { Modal, Upload, Checkbox, Input, Collapse } from "antd"
import type { UploadProps, UploadFile } from "antd"
import { InboxOutlined } from "@ant-design/icons"
import { notify } from "shared/ui/Notifications/notify"
import DataQButton from "shared/ui/Buttons/DataQButton.component"
import { Loader } from "shared/ui/Loader/Loader.component"
import ExportModal from "shared/ui/ExportModal/ExportModal.component"
import { useExportMapStore } from "stores/exportMapStore"
import { useProjectStore } from "stores/projectStore"
import { useExportMapFill } from "features/QueryConsole/api/useExportMapFill"
import { useExportMap } from "features/QueryConsole/api/useExportMap"
import { useAsyncCapable } from "hooks/core/useAsyncCapable"
import { useEngine } from "hooks/api/useEngine"
import { EXPORT_VARIABLE_TYPES } from "types/engineExport.types"
import { downloadCsvAsFile } from "utils/files/downloadUtils"
import {
  ExtractSourceCodesResponse,
  FillExportMapResponse,
} from "api/AI/exportMapFill"
import {
  ExportMapFillOptions,
  ExportMapFillResult,
  SourceCodeInventory,
} from "types/sourceCodes.types"
import { ExportMapVariable } from "types/exportMap.types"
import { exportMapToCSV, parseExportMapCSV } from "./utils/exportMapParser"
import { applyFillRows, ApplyFillRowsResult } from "./utils/exportMapFill"
import {
  failedPartsSentence,
  inventoryCounts,
  shouldReplaceStored,
} from "./utils/inventoryPolicy"
import ExportMapSourceFillReview from "./ExportMapSourceFillReview.component"

const { Dragger } = Upload

export type ExportMapSourceFillStep =
  | "source"
  | "running"
  | "review"
  | "output"

/** "editor" fills the export map in the store; "standalone" fills its own CSV */
export type ExportMapSourceFillMode = "editor" | "standalone"

const DEFAULT_BOOL_PATTERN = "{question}_{option}"

/**
 * Fallbacks for a failure the service did not describe. It usually does: the
 * converter answers `{ success: false, error, message }` as JSON and the
 * Conductor passes that through with HTTP 200, so a rate limit or a model
 * outage arrives as a RESOLVED response whose text is the only thing that
 * tells the user what happened. Those strings are shown verbatim.
 */
const GENERIC_EXTRACT_ERROR = "No codes could be read from the document"
const GENERIC_FILL_ERROR = "The export map could not be matched"
const GENERIC_RUN_ERROR = "Fill from source failed"

const STORED_LOOKUP_WARNING =
  "Could not check for stored source codes; you can still upload the document"

/**
 * Shown on the review step when the uploaded document extracted less than the
 * project already holds: the fill still runs on what was just uploaded, but the
 * stored codes are left alone rather than quietly overwritten.
 */
const STORED_CODES_KEPT_NOTE =
  "Stored codes kept: the uploaded document produced a less complete set"

/**
 * The engine prepends a UTF-8 BOM to its CSV serializations.
 */
const stripBom = (csv: string): string =>
  csv.charCodeAt(0) === 0xfeff ? csv.slice(1) : csv

/**
 * Counts for the stored-codes summary: "12 questions" for an inventory stored
 * before the service reported what its extraction did, and "12 questions,
 * 3 lists, 190 options" once it does.
 */
const describeInventory = (inventory: SourceCodeInventory): string => {
  const counts = inventoryCounts(inventory)
  const questions = `${counts.entries} ${
    counts.entries === 1 ? "question" : "questions"
  }`
  if (!inventory.source?.extraction) return questions
  return (
    `${questions}, ${counts.lists} ${counts.lists === 1 ? "list" : "lists"}` +
    `, ${counts.options} ${counts.options === 1 ? "option" : "options"}`
  )
}

/**
 * "12 March 2026" style date for the stored-inventory summary; falls back to the
 * raw string when the stamp is not a date this browser understands.
 */
const formatExtractedAt = (extractedAt: string): string => {
  const date = new Date(extractedAt)
  return isNaN(date.getTime()) ? extractedAt : date.toLocaleDateString()
}

interface ExportMapSourceFillModalProps {
  open: boolean
  onClose: () => void
  projectId: string
  /**
   * "editor" (default) fills the export map held in the store. "standalone"
   * fills the template datamap passed as `initialCsv` and offers to save it as
   * a profile or download it — the pre-fieldwork path, before any data exists.
   */
  mode?: ExportMapSourceFillMode
  /** Standalone only: the template datamap CSV to fill */
  initialCsv?: string
  /** Testing hook: start on a given step instead of "source" */
  initialStep?: ExportMapSourceFillStep
  /** Testing hook: a fill result to review (used with initialStep "review") */
  initialResult?: ExportMapFillResult
}

/**
 * "Fill from source document": read the question and option codes out of the
 * source questionnaire (.docx) with the AI-converter, match them against an
 * export map, review what was found, then apply the codes.
 *
 * Two modes:
 * - "editor": the export map being edited in the store is filled in place.
 * - "standalone": a template datamap (VARIABLE column and category codes empty)
 *   is filled and then saved as a profile or downloaded — no data needed, so
 *   this runs before fieldwork.
 *
 * Steps: source (stored codes or upload, plus options), running (extract, then
 * match), review, and — standalone only — output (save/download).
 * Errors on the running step return the user to the source step, carrying the
 * service's own message; closing while a run is in flight abandons it.
 */
const ExportMapSourceFillModal: React.FC<ExportMapSourceFillModalProps> = ({
  open,
  onClose,
  projectId,
  mode = "editor",
  initialCsv,
  initialStep = "source",
  initialResult,
}) => {
  const { allVariables, applyFilledCodes } = useExportMapStore()
  const { project } = useProjectStore()
  const {
    extractFromDocument,
    fillFromInventory,
    saveInventory,
    loadInventory,
  } = useExportMapFill()
  const { saveProfile } = useExportMap()
  const { awaitAsyncCapable } = useAsyncCapable()
  const { getDatamapCSV } = useEngine()

  const isStandalone = mode === "standalone"

  const [step, setStep] = useState<ExportMapSourceFillStep>(initialStep)
  const [progress, setProgress] = useState("")
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const [onlyEmpty, setOnlyEmpty] = useState(false)
  const [boolVariablePattern, setBoolVariablePattern] =
    useState(DEFAULT_BOOL_PATTERN)
  // Advanced naming: empty means "leave the server on its own default", so
  // these are sent with the request only when the user fills one in.
  const [otherTextPattern, setOtherTextPattern] = useState("")
  const [valueTwinPattern, setValueTwinPattern] = useState("")
  const [gridBrandPattern, setGridBrandPattern] = useState("")
  const [result, setResult] = useState<ExportMapFillResult | null>(
    initialResult ?? null
  )

  // Standalone: the modal owns the map it is filling (the store holds the
  // export map of a query instance, which does not exist before fieldwork).
  const [standaloneVariables, setStandaloneVariables] = useState<
    ExportMapVariable[]
  >([])
  const [applied, setApplied] = useState<ApplyFillRowsResult | null>(null)
  const [saveProfileModalOpen, setSaveProfileModalOpen] = useState(false)

  // Inventory stored with the project by an earlier extraction (or by the AI
  // conversion that created the questionnaire).
  const [storedInventory, setStoredInventory] =
    useState<SourceCodeInventory | null>(null)
  const [isLoadingStored, setIsLoadingStored] = useState(false)
  const [showUpload, setShowUpload] = useState(false)

  // Review-step notes about the codes this run matched against: whether an
  // upload was refused in favour of the stored codes, and whether the
  // extraction behind them lost part of the document.
  const [keptStoredCodes, setKeptStoredCodes] = useState(false)
  const [partialExtractionNote, setPartialExtractionNote] = useState<
    string | null
  >(null)

  // One warning per opening, however many times the lookup effect re-runs.
  const storedLookupWarnedRef = useRef(false)

  /**
   * Bumped when the modal is closed. A run that was abandoned keeps going on
   * the server (there is nothing to cancel), but its result — and its failure —
   * must not land on a modal the user has walked away from.
   */
  const runIdRef = useRef(0)

  useEffect(() => {
    if (!open) storedLookupWarnedRef.current = false
  }, [open])

  useEffect(() => {
    if (!isStandalone) return
    setStandaloneVariables(
      initialCsv ? parseExportMapCSV(stripBom(initialCsv)) : []
    )
  }, [isStandalone, initialCsv])

  useEffect(() => {
    if (!open || !projectId) return

    let cancelled = false
    const load = async (): Promise<void> => {
      setIsLoadingStored(true)
      try {
        const inventory = await loadInventory(projectId)
        if (!cancelled) setStoredInventory(inventory ?? null)
      } catch (error: unknown) {
        // A missing inventory is the 404 the api layer already turns into null,
        // so anything arriving here is the lookup itself failing. The upload
        // path still works, but silence would leave a user whose codes ARE
        // stored believing the project has none.
        console.warn("Could not load stored source codes:", error)
        if (!cancelled) {
          setStoredInventory(null)
          if (!storedLookupWarnedRef.current) {
            storedLookupWarnedRef.current = true
            notify.toast.warning(STORED_LOOKUP_WARNING)
          }
        }
      } finally {
        if (!cancelled) setIsLoadingStored(false)
      }
    }
    void load()

    return () => {
      cancelled = true
    }
  }, [open, projectId, loadInventory])

  const variables = isStandalone ? standaloneVariables : allVariables

  // What Apply will change, against the map being filled and the chosen options.
  const preview = useMemo(() => {
    if (!result) return null
    const outcome = applyFillRows(variables, result.rows, onlyEmpty)
    return {
      variableCount: outcome.variableCount,
      categoryCount: outcome.categoryCount,
      unknownLabels: outcome.unmatchedLabels,
    }
  }, [variables, result, onlyEmpty])

  const uploadProps: UploadProps = {
    name: "document",
    multiple: false,
    fileList,
    accept: ".docx",
    beforeUpload: (file) => {
      const isDocx =
        file.type ===
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document"
      if (!isDocx) {
        notify.toast.error("Please upload a .docx file only!")
        return false
      }

      const isLt10M = file.size / 1024 / 1024 < 10
      if (!isLt10M) {
        notify.toast.error("File must be smaller than 10MB!")
        return false
      }

      setFileList([file])
      return false // Prevent automatic upload
    },
    onRemove: () => {
      setFileList([])
    },
  }

  /**
   * Closing abandons the run: the modal forgets what it was doing and reopens
   * on the source step. Whatever is in flight finishes on the server, and its
   * result is dropped when it arrives.
   */
  const handleClose = (): void => {
    runIdRef.current++
    setStep(initialStep)
    setProgress("")
    setFileList([])
    setShowUpload(false)
    setResult(initialResult ?? null)
    setKeptStoredCodes(false)
    setPartialExtractionNote(null)
    setApplied(null)
    setSaveProfileModalOpen(false)
    onClose()
  }

  /**
   * Matches the map being filled against an inventory and moves to the review.
   *
   * @param inventory - Source codes to match the map against
   * @param runId - The run this belongs to; a stale one is dropped silently
   */
  /**
   * Editor mode only: the export map generated from data carries no question
   * wording, so fetch the questionnaire datamap with text and attach it per
   * label for the request only (the store is not touched). Standalone mode
   * already receives the template datamap with text. Any failure is silent:
   * the fill still works on labels alone.
   */
  const withQuestionText = async (
    vars: ExportMapVariable[]
  ): Promise<ExportMapVariable[]> => {
    if (isStandalone || !projectId) return vars
    try {
      const res = await getDatamapCSV(
        projectId,
        false, // no section prefix, so labels line up with the map's fullLabel
        EXPORT_VARIABLE_TYPES.NUMBERING,
        undefined,
        true
      )
      const raw =
        typeof res.data === "string" ? res.data : String(res.data ?? "")
      const textByLabel = new Map<string, string>()
      for (const v of parseExportMapCSV(stripBom(raw))) {
        if (v.fieldMetadata) textByLabel.set(v.fullLabel, v.fieldMetadata)
      }
      if (textByLabel.size === 0) return vars
      return vars.map((v) => {
        const text = textByLabel.get(v.fullLabel)
        return text ? { ...v, fieldMetadata: text } : v
      })
    } catch (error) {
      console.warn(
        "Question text unavailable for the fill; matching on labels only",
        error
      )
      return vars
    }
  }

  const runFill = async (
    inventory: SourceCodeInventory,
    runId: number
  ): Promise<void> => {
    const options: ExportMapFillOptions = {
      boolVariablePattern: boolVariablePattern.trim() || DEFAULT_BOOL_PATTERN,
      onlyEmpty,
    }
    const otherText = otherTextPattern.trim()
    if (otherText !== "") options.otherTextPattern = otherText
    const valueTwin = valueTwinPattern.trim()
    if (valueTwin !== "") options.valueTwinPattern = valueTwin
    const gridBrand = gridBrandPattern.trim()
    if (gridBrand !== "") options.gridBrandPattern = gridBrand

    // Whichever path produced these codes, say so in the review when the
    // extraction behind them lost part of the document: the rows it covered
    // are missing outright, not merely uncoded.
    setPartialExtractionNote(failedPartsSentence(inventory))

    setProgress("Matching export map rows…")
    const requestVariables = await withQuestionText(variables)
    const filled = await awaitAsyncCapable<FillExportMapResponse>(
      await fillFromInventory(
        exportMapToCSV(requestVariables),
        inventory,
        options,
        true
      )
    )
    if (!filled?.success) {
      throw new Error(filled?.message || filled?.error || GENERIC_FILL_ERROR)
    }
    if (!filled.data) {
      throw new Error(GENERIC_FILL_ERROR)
    }
    if (runIdRef.current !== runId) return

    setResult(filled.data)
    setStep("review")
  }

  const reportRunFailure = (error: unknown, runId: number): void => {
    console.error("Fill from source failed:", error)
    // Abandoned run: the user closed the modal, so there is nothing to tell them.
    if (runIdRef.current !== runId) return

    const message =
      error instanceof Error && error.message.trim() !== "" ? error.message : ""
    notify.toast.error(
      message ? `${GENERIC_RUN_ERROR}: ${message}` : GENERIC_RUN_ERROR
    )
    setStep("source")
  }

  const handleRun = async (): Promise<void> => {
    if (fileList.length === 0) {
      notify.toast.error("Please select a file first!")
      return
    }
    const actualFile = fileList[0].originFileObj || fileList[0]
    if (!(actualFile instanceof File)) {
      notify.toast.error("Invalid file selected!")
      return
    }
    if (variables.length === 0) {
      notify.toast.error("The export map is empty; nothing to fill.")
      return
    }

    const runId = runIdRef.current
    try {
      setStep("running")
      setProgress("Extracting codes from document…")
      const extracted = await awaitAsyncCapable<ExtractSourceCodesResponse>(
        await extractFromDocument(actualFile, true)
      )
      if (!extracted?.success) {
        throw new Error(
          extracted?.message || extracted?.error || GENERIC_EXTRACT_ERROR
        )
      }
      if (!extracted.data?.inventory) {
        throw new Error(GENERIC_EXTRACT_ERROR)
      }
      if (runIdRef.current !== runId) return
      const { inventory } = extracted.data

      // Keep the inventory with the project for later runs — but only when it
      // is at least as complete as what the project already holds. Both save
      // paths used to write unconditionally, so a run that lost a document
      // chunk could overwrite a full extraction and the next fill would match
      // against half a questionnaire. Storing is not part of this flow's
      // outcome, so a failure is logged rather than shown.
      setKeptStoredCodes(false)
      if (projectId) {
        if (shouldReplaceStored(storedInventory, inventory).replace) {
          saveInventory(projectId, inventory)
            .then(() => setStoredInventory(inventory))
            .catch((error: unknown) => {
              console.warn(
                "Could not store source codes with the project:",
                error
              )
            })
        } else {
          setKeptStoredCodes(true)
        }
      }

      await runFill(inventory, runId)
    } catch (error: unknown) {
      reportRunFailure(error, runId)
    } finally {
      if (runIdRef.current === runId) setProgress("")
    }
  }

  const handleUseStored = async (): Promise<void> => {
    if (!storedInventory) return
    if (variables.length === 0) {
      notify.toast.error("The export map is empty; nothing to fill.")
      return
    }

    const runId = runIdRef.current
    try {
      setStep("running")
      setKeptStoredCodes(false)
      await runFill(storedInventory, runId)
    } catch (error: unknown) {
      reportRunFailure(error, runId)
    } finally {
      if (runIdRef.current === runId) setProgress("")
    }
  }

  const handleApply = (): void => {
    if (!result) return

    if (isStandalone) {
      setApplied(applyFillRows(variables, result.rows, onlyEmpty))
      setStep("output")
      return
    }

    const { variableCount, categoryCount } = applyFilledCodes(
      result.rows,
      onlyEmpty
    )
    const unmatchedTotal =
      result.unmatched.length + (preview?.unknownLabels.length ?? 0)

    notify.toast.success(
      `Filled ${variableCount} ${
        variableCount === 1 ? "variable" : "variables"
      } and ${categoryCount} ${
        categoryCount === 1 ? "category" : "categories"
      } from the source document`
    )
    if (unmatchedTotal > 0) {
      notify.toast.warning(
        `${unmatchedTotal} ${
          unmatchedTotal === 1 ? "row" : "rows"
        } could not be matched to the source document`
      )
    }
    handleClose()
  }

  const handleSaveProfile = async (profileName: string): Promise<void> => {
    if (!applied) return

    // Profile names are lowercase with underscores (same rule as the export map
    // editor, so both paths address the same profile files).
    const sanitizedName = profileName.toLowerCase().replace(/\s+/g, "_")

    try {
      await saveProfile(
        projectId,
        sanitizedName,
        exportMapToCSV(applied.variables)
      )
      notify.toast.success(`Profile "${sanitizedName}" saved`)
      setSaveProfileModalOpen(false)
      handleClose()
    } catch (error: unknown) {
      console.error("Failed to save export map profile:", error)
      notify.toast.error("Failed to save export map profile")
    }
  }

  const handleDownload = (): void => {
    if (!applied) return
    const base = (project.Name || "export_map").trim().replace(/\s+/g, "_")
    downloadCsvAsFile(
      exportMapToCSV(applied.variables),
      `${base}_exportmap.csv`
    )
    notify.toast.success("Export map downloaded")
    handleClose()
  }

  const useStoredCodes = storedInventory !== null && !showUpload

  const renderStoredSummary = (): React.ReactNode => {
    if (!storedInventory) return null
    const { source } = storedInventory
    // The stored codes can be a truncated extraction: say which part of the
    // document is missing, and offer the upload as a re-extract rather than as
    // "a different document", which is not what is wrong with them.
    const failedParts = failedPartsSentence(storedInventory)
    return (
      <div className="space-y-2" data-testid="stored-inventory">
        <p className="text-sm text-slate-700 m-0">
          Stored source codes: <strong>{source.filename}</strong>, extracted{" "}
          {formatExtractedAt(source.extractedAt)},{" "}
          {describeInventory(storedInventory)}
        </p>
        {failedParts && (
          <p
            className="text-sm text-red-600 m-0"
            data-testid="stored-inventory-partial"
          >
            {failedParts}
          </p>
        )}
        <button
          type="button"
          className="text-sm text-[#36c3ed] hover:underline p-0 bg-transparent border-0 cursor-pointer"
          onClick={() => setShowUpload(true)}
        >
          {failedParts
            ? "Re-extract from document"
            : "Upload a different document"}
        </button>
      </div>
    )
  }

  /**
   * One labelled pattern input with its hint (the advanced naming fields).
   */
  const renderPatternField = (
    id: string,
    label: string,
    placeholder: string,
    hint: string,
    value: string,
    onChange: (next: string) => void
  ): React.ReactNode => (
    <div>
      <label
        htmlFor={id}
        className="block text-sm font-medium text-gray-700 mb-1"
      >
        {label}
      </label>
      <Input
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="font-mono"
      />
      <div className="text-xs text-gray-500 mt-1">{hint}</div>
    </div>
  )

  const renderDragger = (): React.ReactNode => (
    <div className="space-y-2">
      <Dragger {...uploadProps}>
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">
          Click or drag the source questionnaire (.docx) to this area
        </p>
        <p className="ant-upload-hint">
          Single .docx file. Maximum file size: 10MB
        </p>
      </Dragger>
      <p className="text-xs text-gray-500 m-0">
        Codes saved to this project for next time
      </p>
    </div>
  )

  const renderSource = () => (
    <div className="space-y-4">
      {isLoadingStored ? (
        <div
          className="flex items-center gap-2 text-sm text-gray-500"
          role="status"
        >
          <Loader fontSize={14} color="#36c3ed" />
          Checking for stored source codes…
        </div>
      ) : useStoredCodes ? (
        renderStoredSummary()
      ) : (
        renderDragger()
      )}

      <Checkbox
        checked={onlyEmpty}
        onChange={(e) => setOnlyEmpty(e.target.checked)}
      >
        Only fill rows without a code
      </Checkbox>

      <div>
        <label
          htmlFor="bool-variable-pattern"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          Variable name for multi-select options
        </label>
        <Input
          id="bool-variable-pattern"
          value={boolVariablePattern}
          onChange={(e) => setBoolVariablePattern(e.target.value)}
          placeholder={DEFAULT_BOOL_PATTERN}
          className="font-mono"
        />
        <div className="text-xs text-gray-500 mt-1">
          {"{question}"} is the source question code and {"{option}"} the option
          code.
        </div>
      </div>

      <Collapse
        ghost
        items={[
          {
            key: "advanced",
            label: "Advanced naming",
            forceRender: true,
            children: (
              <div className="space-y-3">
                {renderPatternField(
                  "other-text-pattern",
                  "Open-response twin",
                  "{question}_{option}oe",
                  "Names the open text row beside the coded answer it belongs to.",
                  otherTextPattern,
                  setOtherTextPattern
                )}
                {renderPatternField(
                  "value-twin-pattern",
                  "Value twin",
                  "{question}",
                  "Names the raw value row of a slider or scale beside its coded row.",
                  valueTwinPattern,
                  setValueTwinPattern
                )}
                {renderPatternField(
                  "grid-brand-pattern",
                  "Grid brand",
                  "{item}{brand}",
                  "Names the brand column of a three-level grid row, from the item and brand codes.",
                  gridBrandPattern,
                  setGridBrandPattern
                )}
                <p className="text-xs text-gray-500 m-0">
                  Leave a field empty to keep the service default.
                </p>
              </div>
            ),
          },
        ]}
      />

      <div className="flex flex-row space-x-2 pt-2">
        <DataQButton className="ml-auto" type="secondary" onClick={handleClose}>
          Cancel
        </DataQButton>
        {useStoredCodes ? (
          <DataQButton type="primary" onClick={handleUseStored}>
            Use stored codes
          </DataQButton>
        ) : (
          <DataQButton
            type="primary"
            onClick={handleRun}
            isDisabled={fileList.length === 0}
          >
            Run
          </DataQButton>
        )}
      </div>
    </div>
  )

  const renderRunning = () => (
    <div className="flex flex-col items-center justify-center py-10">
      <Loader fontSize={32} color="#36c3ed" />
      <p className="mt-3 text-gray-600 text-sm" role="status">
        {progress}
      </p>
      <p className="text-gray-400 text-xs">
        This may take a few moments depending on document size. You can close
        this window; the run is then abandoned.
      </p>
    </div>
  )

  const renderOutput = () => (
    <div className="space-y-4">
      <p className="text-sm text-slate-700 m-0" data-testid="fill-applied">
        Filled <strong>{applied?.variableCount ?? 0}</strong>{" "}
        {applied?.variableCount === 1 ? "variable" : "variables"} and{" "}
        <strong>{applied?.categoryCount ?? 0}</strong>{" "}
        {applied?.categoryCount === 1 ? "category" : "categories"}. Save the
        result as an export map profile for this project, or download it as a
        CSV.
      </p>

      <div className="flex flex-row space-x-2 pt-2">
        <DataQButton className="ml-auto" type="secondary" onClick={handleClose}>
          Cancel
        </DataQButton>
        <DataQButton type="secondary" onClick={handleDownload}>
          Download CSV
        </DataQButton>
        <DataQButton
          type="primary"
          onClick={() => setSaveProfileModalOpen(true)}
        >
          Save as profile…
        </DataQButton>
      </div>
    </div>
  )

  return (
    <>
      <Modal
        title="Fill from source document"
        open={open}
        onCancel={handleClose}
        footer={null}
        width={720}
        centered
        destroyOnHidden
      >
        {step === "source" && renderSource()}
        {step === "running" && renderRunning()}
        {step === "review" && result && preview && (
          <ExportMapSourceFillReview
            result={result}
            preview={preview}
            onApply={handleApply}
            onCancel={handleClose}
            storageNote={keptStoredCodes ? STORED_CODES_KEPT_NOTE : undefined}
            extractionWarning={
              partialExtractionNote
                ? `${partialExtractionNote}; these codes are incomplete`
                : undefined
            }
          />
        )}
        {step === "output" && renderOutput()}
      </Modal>

      <ExportModal
        title="Save Export Map Profile"
        isVisible={saveProfileModalOpen}
        cancel={() => setSaveProfileModalOpen(false)}
        confirm={(profileName) => handleSaveProfile(profileName)}
        message="Enter a name for this export map profile. Profile names are converted to lowercase with underscores."
        ExportButtonText="Save Profile"
      />
    </>
  )
}

export default ExportMapSourceFillModal
